import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

const csvArraySchema = z.array(z.string().trim().min(1));

const resolveTargetTitles = (value: { targetTitle?: string; targetTitles: string[] }) => {
  if (value.targetTitles.length > 0) {
    return value.targetTitles;
  }

  if (value.targetTitle) {
    return [value.targetTitle];
  }

  return [];
};

const createJobProfileBaseSchema = z.object({
  name: z.string().trim().min(1),
  targetTitle: z.string().trim().min(1).optional(),
  targetTitles: csvArraySchema.default([]),
  targetLocations: csvArraySchema.default([]),
  targetSalaryMin: z.coerce.number().int().optional(),
  targetSalaryMax: z.coerce.number().int().optional(),
  remoteOnly: z.coerce.boolean().default(false),
  experienceLevel: z.string().trim().optional(),
  // Up to 5 weighted "main" skills (heavier weight in relevance scoring) plus the
  // unlimited `skills` list (optional skills, manual or CV-extracted).
  mainSkills: csvArraySchema.max(5).default([]),
  skills: csvArraySchema.default([]),
  excludeCompanies: csvArraySchema.default([]),
  excludeKeywords: csvArraySchema.default([]),
  excludeTitles: csvArraySchema.default([]),
  accent: z.string().trim().min(1).max(20).default("primary"),
  baseResumeId: z.string().trim().min(1),
  autoApply: z.coerce.boolean().default(false),
  dailyLimit: z.coerce.number().int().min(1).max(50).default(25),
  // Default 0 = no auto-apply threshold. Was 60, hardcoded here and in three
  // other places while the editor exposed no control for it, so an invisible
  // number decided whether auto-apply drafts got created. See migration
  // 20260820120000_min_score_default_zero.
  minScore: z.coerce.number().int().min(0).max(100).default(0),
});

export const createJobProfileSchema = createJobProfileBaseSchema.superRefine((value, context) => {
  const normalizedTargetTitles = resolveTargetTitles(value);

  if (normalizedTargetTitles.length === 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["targetTitles"],
      message: "Select at least one target title.",
    });
  }

  if (!value.remoteOnly && value.targetLocations.length === 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["targetLocations"],
      message: "Select at least one target location or enable remote-only mode.",
    });
  }

  if (
    value.targetSalaryMin !== undefined &&
    value.targetSalaryMax !== undefined &&
    value.targetSalaryMin > value.targetSalaryMax
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["targetSalaryMax"],
      message: "Maximum salary must be greater than or equal to minimum salary.",
    });
  }
});

export class CreateJobProfileDto extends createZodDto(createJobProfileSchema) {}

export const updateJobProfileSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    targetTitle: z.string().trim().min(1).optional(),
    targetTitles: csvArraySchema.optional(),
    targetLocations: csvArraySchema.optional(),
    targetSalaryMin: z.coerce.number().int().optional(),
    targetSalaryMax: z.coerce.number().int().optional(),
    remoteOnly: z.coerce.boolean().optional(),
    experienceLevel: z.string().trim().optional(),
    mainSkills: csvArraySchema.max(5).optional(),
    skills: csvArraySchema.optional(),
    excludeCompanies: csvArraySchema.optional(),
    excludeKeywords: csvArraySchema.optional(),
    excludeTitles: csvArraySchema.optional(),
    accent: z.string().trim().min(1).max(20).optional(),
    baseResumeId: z.string().trim().min(1).optional(),
    autoApply: z.coerce.boolean().optional(),
    dailyLimit: z.coerce.number().int().min(1).max(50).optional(),
    minScore: z.coerce.number().int().min(0).max(100).optional(),
    isActive: z.coerce.boolean().optional(),
  })
  .superRefine((value, context) => {
    if (
      value.targetSalaryMin !== undefined &&
      value.targetSalaryMax !== undefined &&
      value.targetSalaryMin > value.targetSalaryMax
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetSalaryMax"],
        message: "Maximum salary must be greater than or equal to minimum salary.",
      });
    }
  });

export class UpdateJobProfileDto extends createZodDto(updateJobProfileSchema) {}

export const jobProfileSchema = createJobProfileBaseSchema.extend({
  id: z.string(),
  userId: z.string(),
  targetTitle: z.string().nullable().default(null),
  isActive: z.boolean(),
  lastViewedAt: z.date().nullable().default(null),
  searchPausedAt: z.date().nullable().default(null),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type JobProfileDto = z.infer<typeof jobProfileSchema>;

export const jobProfileSummarySchema = jobProfileSchema.pick({
  id: true,
  name: true,
  targetTitles: true,
  targetTitle: true,
  isActive: true,
  lastViewedAt: true,
  minScore: true,
  accent: true,
  dailyLimit: true,
  createdAt: true,
});

export type JobProfileSummaryDto = z.infer<typeof jobProfileSummarySchema>;
